import { useTranslation } from 'react-i18next';
import type { Child } from '@shared/types';

interface ChildSelectorProps {
  children: Child[];
  mode: 'all' | 'single';
  onModeChange: (mode: 'all' | 'single') => void;
  selectedChildId: string | null;
  onChildSelect: (childId: string) => void;
  /**
   * Предлага ли се „За всички деца" (задача 41). `false` за декларациите с данни, лични за детето
   * (`PER_CHILD_DECLARATION_CODES`) — тогава радиото го няма и се казва защо. Решава хукът.
   */
  allowAllChildren: boolean;
  /**
   * ИЗБРАНОТО дете е в семейството, но декларацията за него вече не чака подпис (задача 43). Тогава
   * обяснението казва ТОВА, не „детето не е в списъка Ви" — то е в списъка, само е подписано.
   * 🔴 Задължителен: пропуснат, той би върнал тихо невярното „не е в списъка" (прегледът, кръг 2).
   */
  selectedChildNotPending: boolean;
}

export function ChildSelector({
  children,
  mode,
  onModeChange,
  selectedChildId,
  onChildSelect,
  allowAllChildren,
  selectedChildNotPending,
}: ChildSelectorProps) {
  const { t } = useTranslation();
  /**
   * Избраното дете (по подразбиране — детето на възлагането) го няма в списъка. 🔴 Тогава нищо не
   * се показва като избрано: `<select>` със стойност извън опциите рисува ПЪРВОТО дете, тоест на
   * екрана би стояло друго дете, а подписът нямаше да има цел (39А §12). Показваме празен избор и
   * казваме защо.
   */
  const избраноЛипсва = mode === 'single' && !children.some((c) => c.childId === selectedChildId);
  if (children.length <= 1 && !избраноЛипсва) {
    // Single child - just display info
    if (children.length === 1) {
      return (
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">{t('signModal.child')}</div>
          <div className="font-medium text-gray-900">
            {children[0].childName} ({children[0].grade})
          </div>
        </div>
      );
    }
    return null;
  }

  const childSelect = (
    <select
      aria-describedby={allowAllChildren ? undefined : 'child-selector-per-child-only'}
      className="input mt-2 w-full"
      value={избраноЛипсва ? '' : selectedChildId || ''}
      onChange={(e) => onChildSelect(e.target.value)}
    >
      {избраноЛипсва && (
        <option value="" disabled>
          {t('signModal.childSelector.pickChild')}
        </option>
      )}
      {children.map((child) => (
        <option key={child.childId} value={child.childId}>
          {child.childName} ({child.grade})
        </option>
      ))}
    </select>
  );
  const missingNotice = избраноЛипсва && (
    <p className="mt-2 text-sm text-yellow-800" role="status">
      {t(
        selectedChildNotPending
          ? 'signModal.childSelector.selectedChildSigned'
          : 'signModal.childSelector.assignmentChildMissing',
      )}
    </p>
  );

  // 🔴 Задача 41: данните са лични за всяко дете → само едно дете наведнъж. Без радиото
  // „За всички"; изборът на дете остава (детето на възлагането е предизбрано, 39А §12).
  // Задача 43 (прегледът на поправката, кр. 2): без радиото и когато НИТО ЕДНО дете не чака —
  // „За всички деца (0)" няма какво да подпише, а превключването към него скриваше известието
  // (напр. „детето на възлагането липсва — свържете се с училището") без път назад.
  if (!allowAllChildren || children.length === 0) {
    return (
      <div className="border border-gray-200 rounded-lg p-4">
        <label className="block">
          <span className="font-medium text-gray-900">{t('signModal.child')}</span>
          {childSelect}
        </label>
        {!allowAllChildren && (
          <p id="child-selector-per-child-only" className="mt-2 text-sm text-gray-600">
            {t('signModal.childSelector.perChildOnly')}
          </p>
        )}
        {missingNotice}
      </div>
    );
  }

  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h3 className="font-medium text-gray-900 mb-3">
        {t('signModal.childSelector.question')}
      </h3>

      {/* All Children Option */}
      <label className="flex items-start gap-3 mb-3 cursor-pointer">
        <input
          type="radio"
          checked={mode === 'all'}
          onChange={() => onModeChange('all')}
          className="mt-1"
        />
        <div>
          <span className="font-medium">
            {t('signModal.childSelector.allChildren', { count: children.length })}
          </span>
          <div className="ml-6 mt-2 space-y-1">
            {children.map((child) => (
              <div key={child.childId} className="text-sm text-gray-600">
                ├─ {child.childName} ({child.grade})
              </div>
            ))}
          </div>
        </div>
      </label>

      {/* Single Child Option */}
      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="radio"
          checked={mode === 'single'}
          onChange={() => onModeChange('single')}
          className="mt-1"
        />
        <div className="flex-1">
          <span className="font-medium">{t('signModal.childSelector.singleChild')}</span>
          {mode === 'single' && childSelect}
          {missingNotice}
        </div>
      </label>
    </div>
  );
}
